/**
 * Stream filters helpers – aligned with athlete-mobile-app Watch filters.
 * Builds StreamFilterInput for streamsV2 / getPersonalizedStreams from UI filter state.
 */

import type { StreamFilterInput } from 'api/graphql-global-types';
import {
  StreamOrderBy,
  SortDirection,
  StreamStatus,
  PublicStreamCategory,
  StreamPriceCategory,
  StoreStatus,
} from 'api/graphql-global-types';

/** Watch "Type" tab: All Streams | PPV | Interviews (mobile) */
export type WatchStreamType = 'all' | 'ppv' | 'streams' | 'interviews';

/** Single sort option for streams (matches mobile WatchFiltersScreen) */
export type StreamSortOption = {
  id: number;
  label: string;
  orderBy: StreamOrderBy;
  direction: SortDirection;
};

/** Status option: label + stream statuses (matches mobile STATUS_OPTIONS) */
export type StreamStatusOption = {
  id: number;
  label: string;
  statuses: StreamStatus[];
};

/** Params to build stream filter input (mirrors mobile filter state) */
export type BuildStreamFilterInputParams = {
  /** Watch type: all (no type filter), ppv (paid), streams (free), interviews */
  type?: WatchStreamType;
  /** Min/max price; only applied when type is 'ppv' or when explicitly filtering by price */
  priceRange?: [number, number];
  /** Sort order */
  orderBy?: StreamOrderBy;
  direction?: SortDirection;
  /** Status filter: Live, Upcoming, Past (can combine) */
  streamStatus?: StreamStatus[];
  /** Date range [from, to] ISO strings */
  scheduleDate?: [string | null, string | null];
  /** Search term */
  searchTerm?: string;
  /** Pagination */
  limit?: number;
  offset?: number;
  /** Store slug (e.g. athlete page) */
  storeSlug?: string | null;
  /** Store IDs (e.g. athleteIds from Athletes filter) */
  storeIds?: string[];
  /** Include Paused/Interrupted when user selects Live */
  withCopies?: boolean;
  /** Timezone codes filter */
  tzcodes?: string[];
};

/** Sort options for streams – matches mobile "Sort By" (WatchFiltersScreen STREAM_SORT_OPTIONS) */
export const STREAM_SORT_OPTIONS: StreamSortOption[] = [
  {
    id: 1,
    label: 'Price: Highest first',
    orderBy: StreamOrderBy.Price,
    direction: SortDirection.DESC,
  },
  {
    id: 2,
    label: 'Price: Lowest first',
    orderBy: StreamOrderBy.Price,
    direction: SortDirection.ASC,
  },
  {
    id: 3,
    label: 'Name: (A-Z)',
    orderBy: StreamOrderBy.Name,
    direction: SortDirection.ASC,
  },
  {
    id: 4,
    label: 'Name: (Z-A)',
    orderBy: StreamOrderBy.Name,
    direction: SortDirection.DESC,
  },
  {
    id: 5,
    label: 'Date: Oldest first',
    orderBy: StreamOrderBy.ScheduleDateStrict,
    direction: SortDirection.ASC,
  },
  {
    id: 6,
    label: 'Date: Newest first',
    orderBy: StreamOrderBy.ScheduleDateStrict,
    direction: SortDirection.DESC,
  },
];

/** Status options – matches mobile "Status" (Live / Upcoming / Past) */
export const STREAM_STATUS_OPTIONS: StreamStatusOption[] = [
  {
    id: 1,
    label: 'Live',
    statuses: [
      StreamStatus.Active,
      StreamStatus.Paused,
      StreamStatus.Interrupted,
    ],
  },
  { id: 2, label: 'Upcoming', statuses: [StreamStatus.Scheduled] },
  { id: 3, label: 'Past', statuses: [StreamStatus.Ended] },
];

/** Stream statuses offered in the Streams page status filter (Live / Upcoming / Past) */
export const STREAM_STATUS_FILTER_VALUES: readonly StreamStatus[] = [
  StreamStatus.Active,
  StreamStatus.Scheduled,
  StreamStatus.Ended,
] as const;

/** Default stream statuses when no status filter is applied (all) */
export const DEFAULT_STREAM_STATUSES: StreamStatus[] = [
  StreamStatus.Active,
  StreamStatus.Scheduled,
  StreamStatus.Paused,
  StreamStatus.Interrupted,
  StreamStatus.Ended,
];

/** When user selects "Live", backend typically expects Active + Paused + Interrupted */
export function expandLiveStatus(statuses: StreamStatus[]): StreamStatus[] {
  if (statuses.includes(StreamStatus.Active)) {
    const next = [...statuses];
    if (!next.includes(StreamStatus.Paused)) next.push(StreamStatus.Paused);
    if (!next.includes(StreamStatus.Interrupted))
      next.push(StreamStatus.Interrupted);
    return next;
  }
  return statuses;
}

/**
 * Builds StreamFilterInput for streamsV2 from filter params.
 * Matches mobile query usage (WatchPPV, WatchStreams, WatchInterviews, WatchFiltersScreen).
 */
export function buildStreamFilterInput(
  params: BuildStreamFilterInputParams
): StreamFilterInput {
  const {
    type = 'all',
    priceRange,
    orderBy = StreamOrderBy.ScheduleDateStrict,
    direction = SortDirection.DESC,
    streamStatus,
    scheduleDate,
    searchTerm,
    limit = 16,
    offset = 0,
    storeSlug,
    storeIds,
    withCopies = false,
    tzcodes,
  } = params;

  const statuses = streamStatus?.length
    ? expandLiveStatus(streamStatus)
    : DEFAULT_STREAM_STATUSES;

  const input: StreamFilterInput = {
    limit,
    offset,
    orderBy,
    direction,
    streamStatus: statuses,
    storeStatus: StoreStatus.Active,
    withCopies,
    ...(searchTerm?.trim() && { searchTerm: searchTerm.trim() }),
    ...(storeSlug && { storeSlug }),
    ...(storeIds?.length ? { storeIds } : {}),
    ...(tzcodes?.length ? { tzcodes } : {}),
  };

  // Type: PPV = paid streams only; streams = free only; interviews = Interview category; all = no type/price category filter
  if (type === 'ppv') {
    input.publicStreamCategory = [PublicStreamCategory.Stream];
    input.priceCategories = [StreamPriceCategory.Paid];
  } else if (type === 'streams') {
    input.publicStreamCategory = [PublicStreamCategory.Stream];
    input.priceCategories = [StreamPriceCategory.Free];
  } else if (type === 'interviews') {
    input.publicStreamCategory = [PublicStreamCategory.Interview];
    // no priceCategories – interviews can be free or paid
  }
  // type === 'all': no publicStreamCategory or priceCategories

  // Price range (from slider) – only applied when we have a range
  if (
    priceRange?.length === 2 &&
    priceRange[0] != null &&
    priceRange[1] != null
  ) {
    input.price = { from: priceRange[0], to: priceRange[1] };
  }

  // Date range
  if (scheduleDate?.length === 2 && scheduleDate[0] && scheduleDate[1]) {
    input.scheduleDate = { from: scheduleDate[0], to: scheduleDate[1] };
  }

  return input;
}
